import { useCallback, useEffect, useState } from "react";
import { EffectRow, listEffects } from "../api";

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === "ready"
      ? "bg-lemonade text-black"
      : status === "error"
      ? "bg-red-600 text-ivory"
      : status === "processing"
      ? "bg-blue-500 text-ivory"
      : "bg-stone text-charcoal";
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}
    >
      {status}
    </span>
  );
}

export default function AdminEffectsHistory() {
  const [effects, setEffects] = useState<EffectRow[]>([]);
  const [listError, setListError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const rows = await listEffects();
      setEffects(rows);
      setListError(null);
    } catch (e) {
      setListError(e instanceof Error ? e.message : "Liste indisponible");
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Poll every 4s while at least one effect is non-terminal.
  useEffect(() => {
    const pending = effects.some(
      (e) => e.status === "queued" || e.status === "processing"
    );
    if (!pending) return;
    const t = setInterval(() => void reload(), 4000);
    return () => clearInterval(t);
  }, [effects, reload]);

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-bold text-charcoal">Effets effectués</h2>
        <button
          type="button"
          onClick={() => void reload()}
          className="text-xs text-concrete hover:text-charcoal"
        >
          Rafraîchir
        </button>
      </div>

      {listError && (
        <div className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {listError}
        </div>
      )}

      {effects.length === 0 && !listError && (
        <p className="text-sm text-concrete">
          Aucun effet pour le moment. Compose le premier depuis l&apos;onglet
          Effets.
        </p>
      )}

      <ul className="space-y-3">
        {effects.map((e) => (
          <li
            key={e.effectId}
            className="rounded-lg border border-stone bg-white p-4"
          >
            <div className="mb-2 flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-medium text-charcoal">
                  {e.effectType}
                </div>
                <div className="font-mono text-xs text-concrete">
                  {e.effectId.slice(0, 16)}…
                </div>
                <div className="text-xs text-concrete">
                  {new Date(e.createdAt).toLocaleString("fr-FR")}
                </div>
              </div>
              <StatusBadge status={e.status} />
            </div>

            <div className="mb-2 text-xs text-concrete">
              {e.params.preset} · début {e.params.startTime}s · durée{" "}
              {e.params.duration}s
            </div>

            {e.status === "ready" && e.resultUrl && (
              <div className="space-y-2">
                <video
                  src={e.resultUrl}
                  controls
                  className="w-full max-w-xs rounded-md bg-black"
                />
                <a
                  href={e.resultUrl}
                  download
                  className="inline-block text-sm text-charcoal underline hover:text-black"
                >
                  Télécharger
                </a>
              </div>
            )}

            {e.status === "error" && (
              <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
                {e.errorMessage || "Erreur inconnue"}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
